import { useEffect, useMemo, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { CarCard } from '../../components/ui/CarCard.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { SupportLink } from '../../components/ui/SupportLink.js';
import { useAuth } from '../../context/AuthContext.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useNearestLocationId } from '../../hooks/useNearestLocationId.js';
import { clearHostFilter, readHostFilter } from '../../services/hostFilter.js';
import {
  EMPTY_FILTERS,
  type FilterState,
  SearchFilters,
  activeFilterCount,
} from './SearchFilters.js';
import { groupCarsByLocation } from './groupCarsByLocation.js';

interface Car {
  _id: string;
  name: string;
  transmission: string;
  fuel_type: string;
  seat_capacity: number;
  // 127: the join key + fallback label groupCarsByLocation reads (research.md D1).
  location: { location_id: string; city_name: string };
  images?: Array<{ url: string; is_primary?: boolean }>;
  // 050: browsing display rate from /cars; null when the car has no usable rate card.
  pricing?: {
    currency_symbol: string;
    per_hour_minor: number | null;
    per_day_minor: number | null;
  } | null;
  // 057: browsing rating from /cars; null when the car has no reviews.
  rating?: { average: number; count: number } | null;
}

interface SearchProps {
  location_id?: string;
  pickup_at?: string;
  return_at?: string;
  path?: string;
}

interface LocationsResponse {
  items: { _id: string; name: string }[];
}

/** A price the shopper typed in RM, as whole minor units. Null when there was nothing usable. */
function toMinorUnits(major: string): number | null {
  if (!major.trim()) return null;
  const n = Number(major);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

/** 058: the orderings the API accepts. Anything else falls back server-side. 363: Recommended leads, and is the default. */
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'newest', label: 'Newest' },
  { value: 'year', label: 'Model year' },
  { value: 'seats', label: 'Seats' },
];

export function SearchResultsPage({ location_id, pickup_at, return_at }: SearchProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [sort, setSort] = useState('recommended');
  /**
   * 058: the filters IN EFFECT. The panel keeps its own draft and hands a whole set back
   * on Apply, so this changes once per apply — not once per control, which is what the
   * previous per-control effect did (FR-014).
   */
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  // 343 (#937): a host's share link, kept on the device until cleared. Read once per visit to the page.
  const [hostName, setHostName] = useState<string | null>(() => readHostFilter());
  const showAllCars = () => {
    clearHostFilter();
    setHostName(null);
  };

  // 330 (US4-1, FR-012): a customer with a campus searches it, whatever the address names — the bottom
  // nav opens this page with no location, which used to mean "Any location". The platform enforces
  // the campus anyway; this names it and stops the grouping and its geolocation request.
  const { user, refreshUser } = useAuth();
  const campus = user?.campus ?? null;
  const searchLocationId = campus?._id ?? location_id;

  // 127: "Any location" (no location_id) is the only case that groups results by pickup
  // point — a search that already named one location has only one location's worth of
  // cars, so grouping would add a heading over nothing (spec FR-003).
  const grouping = !searchLocationId;

  /**
   * 064: the query the platform is being asked, built in one place and handed to `useFetch`.
   *
   * This screen used to run its own effect ending in `.catch(() => setLoading(false))`, so a
   * failed read left `cars` empty and the render fell through to "No cars available for the
   * selected criteria" — an outage was indistinguishable from an empty fleet, on the app's
   * busiest screen. It now reads the way every other screen does (FR-015).
   */
  const carsPath = useMemo(() => {
    const params = new URLSearchParams();
    if (searchLocationId) params.set('location_id', searchLocationId);
    if (pickup_at) params.set('pickup_at', pickup_at);
    if (return_at) params.set('return_at', return_at);
    if (filters.transmission) params.set('transmission', filters.transmission);
    if (filters.fuel_type) params.set('fuel_type', filters.fuel_type);
    if (filters.brand_id) params.set('brand_id', filters.brand_id);
    if (filters.category_id) params.set('category_id', filters.category_id);
    if (filters.feature_ids.length) params.set('feature_ids', filters.feature_ids.join(','));
    // The panel collects RM; the API takes minor units. Converting here — the one place
    // the query is built — keeps "what the shopper typed" and "what the API means" from
    // being the same number with two meanings.
    const minMinor = toMinorUnits(filters.min_price_hour);
    const maxMinor = toMinorUnits(filters.max_price_hour);
    if (minMinor !== null) params.set('min_price_hour_minor', String(minMinor));
    if (maxMinor !== null) params.set('max_price_hour_minor', String(maxMinor));
    if (filters.min_rating) params.set('min_rating', filters.min_rating);
    if (sort) params.set('sort', sort);
    if (hostName) params.set('host', hostName);
    // 127 FR-009: an ungrouped, location-scoped search keeps the server's own default page
    // size — only grouping needs the larger fetch, so no section is thinned by an arbitrary
    // page-1 cut (research.md D3; `100` is the server's own ceiling, `routes.ts:122`).
    if (grouping) params.set('limit', '100');

    return `/cars?${params.toString()}`;
  }, [searchLocationId, pickup_at, return_at, filters, sort, grouping, hostName]);

  const carsRead = useFetch<{ items: Car[] }>(carsPath);
  const { data, status, retry } = carsRead;
  const cars = data?.items ?? [];
  const activeCount = activeFilterCount(filters);
  // 330: the campus was cleared while this page was open. Re-reading the account brings the chooser.
  const campusRequired = (data as { code?: string } | null)?.code === 'CAMPUS_REQUIRED';
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshUser is re-created each render
  useEffect(() => {
    if (campusRequired) void refreshUser();
  }, [campusRequired]);
  const campusClosed = Boolean(campus && !campus.is_active);
  const hostLinkInactive = (data as { code?: string } | null)?.code === 'HOST_LINK_NOT_ACTIVE';

  // 127: fetched only while grouping applies — a location-scoped search never pays for a
  // read it has no use for. Reuses the same public endpoint HomePage.tsx already calls.
  const locationsRead = useFetch<LocationsResponse>(grouping ? '/locations' : null);
  const locationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of locationsRead.data?.items ?? []) map.set(loc._id, loc.name);
    return map;
  }, [locationsRead.data]);
  // 127: gated on `grouping` — a location-scoped search has no use for this result and
  // must not trigger a real browser geolocation permission request for nothing.
  const nearestLocationId = useNearestLocationId(grouping);
  const sections = useMemo(
    () => (grouping ? groupCarsByLocation(cars, locationNames, nearestLocationId) : []),
    [grouping, cars, locationNames, nearestLocationId],
  );

  return (
    // 137: bounded to exactly the space the shell leaves available (100dvh minus its 132px
    // chrome, unchanged since 134/136) and clipped, not merely floored — the outer page never
    // overflows, so the shell's bell (above this page, outside it) is never displaced by
    // scrolling. Only the results region below the header scrolls.
    <div className="page-bound bg-bg pt-4 px-4 flex flex-col">
      {/* 208 (#308): had no back control at all before this feature. */}
      <PageHeader title="Available Cars" fallback="/" />

      {campus && (
        <p data-testid="search-campus" className="mb-3 text-sm text-gray-400">
          Your campus: <span className="text-text">{campus.name}</span>
        </p>
      )}

      {hostName && !hostLinkInactive && (
        <div
          data-testid="host-filter-banner"
          className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-surface-mid px-3 py-2 text-sm text-text"
        >
          <span>Cars from {hostName}</span>
          <Button variant="ghost" onClick={showAllCars}>
            Show all cars
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" onClick={() => setPanelOpen(true)}>
          {activeCount > 0 ? `Filters (${activeCount})` : 'Filters'}
        </Button>

        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs text-gray-400" htmlFor="search-sort">
            Sort
          </label>
          <select
            id="search-sort"
            className="bg-surface-mid text-sm text-text rounded-lg px-3 py-1.5"
            value={sort}
            onChange={(e) => setSort((e.target as HTMLSelectElement).value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* 363 (design 2a): what Recommended means. A native picker cannot show it under the option, so it sits here. */}
      {sort === 'recommended' && (
        <p data-testid="sort-explainer" className="-mt-2 mb-3 text-right text-xs text-text-muted">
          Best-rated first. New cars get a fair start.
        </p>
      )}

      <SearchFilters
        open={panelOpen}
        value={filters}
        onApply={(next) => {
          setFilters(next);
          setPanelOpen(false);
        }}
        onDismiss={() => setPanelOpen(false)}
      />

      <div
        data-testid="page-scroll-region"
        className="flex-1 overflow-y-auto overscroll-y-contain pb-4"
      >
        {status === 'loading' ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : status !== 'ok' ? (
          /* 064: a read that did not produce a listing says so. Reaching the empty copy below
           now means the platform really answered with nothing (FR-002, FR-012). */
          <ResultState state={carsRead} onRetry={retry} />
        ) : campusClosed ? (
          /* 330 edge case: a deactivated campus stays on the customer, who sees no cars there
           until staff reassign them — say why, and how to ask. */
          <p data-testid="search-campus-closed" className="py-16 text-center text-gray-400">
            Your campus isn't taking bookings right now.{' '}
            <SupportLink className="text-primary underline">Ask Carii to change it</SupportLink>
          </p>
        ) : hostName && hostLinkInactive ? (
          /* 343: a link that was renamed, removed, or whose host left. Never another host's cars under its name. */
          <div className="flex flex-col items-center gap-3 py-16">
            <p data-testid="host-filter-inactive" className="text-center text-gray-400">
              The link “{hostName}” is no longer active.
            </p>
            <Button variant="ghost" onClick={showAllCars}>
              Show all cars
            </Button>
          </div>
        ) : hostName && cars.length === 0 ? (
          /* 343: the campus still applies (330), so a host's cars elsewhere never show; say so. */
          <div className="flex flex-col items-center gap-3 py-16">
            <p data-testid="host-filter-empty" className="text-center text-gray-400">
              None of {hostName}’s cars are at {campus?.name ?? 'this location'} for this search.
            </p>
            <Button variant="ghost" onClick={showAllCars}>
              Show all cars
            </Button>
          </div>
        ) : cars.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-center text-gray-400">No cars available for the selected criteria</p>
            {/* 058: filtered down to nothing is the one place a shopper most needs a way out,
              and guessing which control did it is not one (FR-015). */}
            {activeCount > 0 && (
              <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear all filters
              </Button>
            )}
          </div>
        ) : grouping ? (
          <div className="flex flex-col gap-6">
            {sections.map((section) => (
              <div key={section.locationId}>
                <h2 className="text-sm font-semibold text-gray-400 mb-2">
                  {section.name} · {section.cars.length} car{section.cars.length === 1 ? '' : 's'}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {section.cars.map((car) => (
                    <CarCard key={car._id} car={car} searchWindow={{ pickup_at, return_at }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cars.map((car) => (
              <CarCard key={car._id} car={car} searchWindow={{ pickup_at, return_at }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
